/**
 * Pure helpers for transforming between the flat rule array used by the
 * MatchingRules API and the pass-grouped structure used by the UI.
 *
 * Wire format (flat):
 *   [{
 *     id: string|number,
 *     mergePass: number,
 *     fieldName: string,
 *     allowBlanks: boolean,
 *     mustBeBlank: boolean,
 *     updateDetails: boolean,
 *     isActive: boolean
 *   }]
 *
 * UI format (grouped):
 *   [{
 *     mergePass: number,
 *     updateDetails: boolean,
 *     updateDetailsWasInconsistent: boolean,
 *     _uiKey: string,
 *     rules: [{
 *       id: string|number,
 *       fieldName: string,
 *       allowBlanks: boolean,
 *       mustBeBlank: boolean,
 *       isActive: boolean
 *     }]
 *   }]
 *
 * Note: updateDetails is pass-scoped on the wire (the API stores it on every
 * row but the rows in a pass are always in sync). The UI promotes it to the
 * pass level. If wire data ever has rows in disagreement within a pass, the
 * value from the lowest-id rule wins (deterministic, stable across reloads).
 */

let uiKeyCounter = 0
function nextUiKey () { return `pass-uikey-${++uiKeyCounter}` }

export function groupRulesByPass (rules) {
	if (!Array.isArray(rules) || rules.length === 0) return []

	const byPass = new Map()
	for (const rule of rules) {
		const key = rule.mergePass
		if (!byPass.has(key)) byPass.set(key, [])
		byPass.get(key).push(rule)
	}

	const passes = []
	// mergePass arrives as int from the API, but coerce defensively in case of string JSON.
	const sortedKeys = [...byPass.keys()].sort((a, b) => Number(a) - Number(b))
	for (const key of sortedKeys) {
		const passRules = byPass.get(key)
		const updateDetails = pickPassScopedUpdateDetails(passRules)
		passes.push({
			mergePass: key,
			_uiKey: nextUiKey(),
			updateDetails,
			updateDetailsWasInconsistent: passRules.some(r => r.updateDetails !== updateDetails),
			rules: passRules.map(({ mergePass, updateDetails, ...rest }) => rest)
		})
	}
	return passes
}

export function flattenPasses (passes) {
	const out = []
	let mergePassCounter = 0
	for (const pass of passes) {
		mergePassCounter += 1
		// Renumber contiguously starting at 1. The original pass.mergePass is
		// discarded — pass numbers are an ordering, not an identity, and any
		// gaps from reorders or deletes are closed here.
		const mergePass = mergePassCounter
		// Passes with no rules produce no output rows — empty passes are invalid
		// on the wire and the UI strips them at delete-time (see PassList).
		for (const rule of pass.rules) {
			out.push({
				...rule,
				mergePass,
				updateDetails: pass.updateDetails
			})
		}
	}
	return out
}

function pickPassScopedUpdateDetails (rules) {
	if (rules.length === 0) return false
	// Lowest-id wins. Matching-rule ids are numeric, so compare numerically when both
	// coerce to finite numbers (otherwise "10" < "2" lexicographically picks the wrong
	// rule); fall back to localeCompare for string/GUID ids. New rules (id === null)
	// sort to the end.
	const sorted = [...rules].sort((a, b) => {
		if (a.id == null && b.id == null) return 0
		if (a.id == null) return 1
		if (b.id == null) return -1
		const na = Number(a.id)
		const nb = Number(b.id)
		if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb
		return String(a.id).localeCompare(String(b.id))
	})
	return Boolean(sorted[0].updateDetails)
}
